import { LOANS_COPY } from "./estate.constants.ts";
import { pickerLabel } from "./loansConsign.model.ts";
import { useLoansConsignController } from "./useLoansConsignController.ts";
import "./estate.css";

/**
 * The owner's two acts — lend, and consign — with the rail of what each has
 * produced. Both submits end in a definer that resolves the counterparty with
 * one opaque refusal; the forms mirror only what a form can honestly know.
 */
export function LoansConsign() {
  const {
    view,
    tab,
    switchTab,
    loanDraft,
    setLoanDraft,
    consignDraft,
    setConsignDraft,
    refusal,
    confirmation,
    working,
    submitLoan,
    submitConsign,
    dispatch,
  } = useLoansConsignController();

  if (view.kind === "loading") {
    return (
      <p className="estate__note" role="status">
        {view.message}
      </p>
    );
  }

  if (view.kind === "error") {
    return (
      <section className="estate__note estate__note--error" role="alert">
        <h2 className="estate__note-title">{view.title}</h2>
        <p>{view.message}</p>
      </section>
    );
  }

  const workPicker = (value: string, onPick: (workId: string) => void) => (
    <label className="estate__input">
      <span>{LOANS_COPY.work}</span>
      <select onChange={(event) => onPick(event.target.value)} value={value}>
        <option value="">{LOANS_COPY.pickWork}</option>
        {view.works.map((work) => (
          <option key={work.workId} value={work.workId}>
            {pickerLabel(work)}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="estate__split">
      <section className="estate__pane">
        <div className="estate__tabs" role="tablist">
          <button
            aria-selected={tab === "loan"}
            className={`estate__tab${tab === "loan" ? " estate__tab--open" : ""}`}
            onClick={() => switchTab("loan")}
            role="tab"
            type="button"
          >
            {LOANS_COPY.loanTab}
          </button>
          <button
            aria-selected={tab === "consign"}
            className={`estate__tab${tab === "consign" ? " estate__tab--open" : ""}`}
            onClick={() => switchTab("consign")}
            role="tab"
            type="button"
          >
            {LOANS_COPY.consignTab}
          </button>
        </div>

        {confirmation && (
          <p className="estate__confirmation" role="status">
            {confirmation}
          </p>
        )}

        {tab === "loan" ? (
          <form
            className="estate__form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitLoan();
            }}
          >
            {workPicker(loanDraft.workId, (workId) => setLoanDraft({ workId }))}
            <label className="estate__input">
              <span>{LOANS_COPY.venue}</span>
              <input
                onChange={(event) => setLoanDraft({ venue: event.target.value })}
                placeholder={LOANS_COPY.venuePlaceholder}
                value={loanDraft.venue}
              />
            </label>
            <label className="estate__input">
              <span>{LOANS_COPY.borrower}</span>
              <input
                onChange={(event) => setLoanDraft({ borrower: event.target.value })}
                placeholder={LOANS_COPY.borrowerPlaceholder}
                value={loanDraft.borrower}
              />
            </label>
            <p className="estate__hint">{LOANS_COPY.borrowerNote}</p>
            <div className="estate__form-grid">
              <label className="estate__input">
                <span>{LOANS_COPY.loanStart}</span>
                <input
                  onChange={(event) => setLoanDraft({ startsOn: event.target.value })}
                  type="date"
                  value={loanDraft.startsOn}
                />
              </label>
              <label className="estate__input">
                <span>{LOANS_COPY.loanEnd}</span>
                <input
                  onChange={(event) => setLoanDraft({ endsOn: event.target.value })}
                  type="date"
                  value={loanDraft.endsOn}
                />
              </label>
            </div>
            <label className="estate__input">
              <span>{LOANS_COPY.purpose}</span>
              <input
                onChange={(event) => setLoanDraft({ purpose: event.target.value })}
                placeholder={LOANS_COPY.purposePlaceholder}
                value={loanDraft.purpose}
              />
            </label>
            {refusal && (
              <p className="estate__refusal" role="alert">
                {refusal}
              </p>
            )}
            <button className="estate__primary" disabled={working} type="submit">
              {LOANS_COPY.initiateLoan}
            </button>
            <p className="estate__hint">{LOANS_COPY.loanFootnote}</p>
          </form>
        ) : (
          <form
            className="estate__form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitConsign();
            }}
          >
            {workPicker(consignDraft.workId, (workId) => setConsignDraft({ workId }))}
            <label className="estate__input">
              <span>{LOANS_COPY.gallery}</span>
              <input
                onChange={(event) => setConsignDraft({ ondoId: event.target.value })}
                placeholder={LOANS_COPY.galleryPlaceholder}
                value={consignDraft.ondoId}
              />
            </label>
            <p className="estate__hint">{LOANS_COPY.galleryNote}</p>
            <div className="estate__form-grid">
              <label className="estate__input">
                <span>{LOANS_COPY.asking}</span>
                <input
                  onChange={(event) => setConsignDraft({ asking: event.target.value })}
                  placeholder={LOANS_COPY.askingPlaceholder}
                  value={consignDraft.asking}
                />
              </label>
              <label className="estate__input">
                <span>{LOANS_COPY.currency}</span>
                <input
                  onChange={(event) => setConsignDraft({ currency: event.target.value })}
                  value={consignDraft.currency}
                />
              </label>
            </div>
            <label className="estate__input">
              <span>{LOANS_COPY.note}</span>
              <input
                onChange={(event) => setConsignDraft({ note: event.target.value })}
                placeholder={LOANS_COPY.notePlaceholder}
                value={consignDraft.note}
              />
            </label>
            {refusal && (
              <p className="estate__refusal" role="alert">
                {refusal}
              </p>
            )}
            <button className="estate__primary" disabled={working} type="submit">
              {LOANS_COPY.consignWork}
            </button>
            <p className="estate__hint">{LOANS_COPY.consignFootnote}</p>
          </form>
        )}
      </section>

      <aside className="estate__rail" aria-label={LOANS_COPY.railTitle}>
        <h2 className="estate__rail-title">{LOANS_COPY.railTitle}</h2>
        {view.railEmpty ? (
          <p className="estate__hint">{view.railEmpty}</p>
        ) : (
          <ul className="estate__rail-rows">
            {view.rail.map((row) => (
              <li
                className={`estate__rail-row${row.live ? "" : " estate__rail-row--done"}`}
                key={row.key}
              >
                <span className={`estate__badge estate__badge--${row.badge}`}>
                  {row.badge === "loan" ? "LOAN" : "CONSIGN"}
                </span>
                <span className="estate__row-body">
                  <span className="estate__row-title">{row.title}</span>
                  <span className="estate__row-meta">{row.line}</span>
                  {row.detail && <span className="estate__row-meta">{row.detail}</span>}
                </span>
                <span className="estate__rail-state">{row.state}</span>
                {row.dispatchableLoanId !== null && (
                  <button
                    type="button"
                    className="estate__ghost estate__ghost--rail"
                    disabled={working}
                    onClick={() => void dispatch(row.dispatchableLoanId!)}
                    title={LOANS_COPY.dispatchHint}
                  >
                    {LOANS_COPY.dispatch}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </aside>
    </div>
  );
}
